import React from 'react';
import { DeleteOutlined, Email } from '@mui/icons-material';
import { Box, Chip, Grid, styled, SxProps, Theme, Tooltip, tooltipClasses, TooltipProps, Typography } from '@mui/material';
import { GridCellParams, GridColumns, GridRenderCellParams } from '@mui/x-data-grid';
import { useEmailStatement } from 'api/useEmailStatement';
import { IconButton } from 'components/Elements/Buttons/IconButton';
import { NoRowsOverlay } from 'components/Elements/Buttons/NoRowsOverlay';
import { ParticipateToggleButton } from 'components/Elements/Buttons/ParticipateToggleButton';
import { EmailStatementDialog } from 'components/Elements/Dialogs/EmailStatementDialog';
import { Toast } from 'components/Elements/Feedback/Toast';
import { NumberInput } from 'components/Elements/Inputs/NumberInput';
import { formatProviderName } from 'utils/formatProviderName';
import { canEditPaymentAmount, calculateTotal, formatToUSD, isValidPaymentAmount, NON_PARTICIPATING_FEE } from 'utils/participation';
import { useAssessmentRowActions } from 'utils/useAssessmentRowActions';
import { AssessmentPaymentFooter } from './AssessmentPaymentFooter';
import { DataGrid } from './DataGrid';
import { MaskedEmailField } from './MaskedEmailField';

const CustomDataGrid = styled(DataGrid)(({ theme }) => ({
  '.MuiDataGrid-main': {
    ' .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    ' .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
  },
  '.MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  '.MuiDataGrid-row.row-striped': {
    backgroundColor: theme.palette.grey[50],
  },
  '.MuiDataGrid-cell.payment-amount-editing': {
    paddingRight: 0,
  },
  maxWidth: '100%',
}));

export interface PhysicianSearchGridProps {
  rows: Array<PaymentItem & { isNew?: boolean }>;
  setRows?: React.Dispatch<React.SetStateAction<Array<PaymentItem & { isNew?: boolean }>>>;
  handleDeleteClick?: (id: any) => void;
  disableEditAndRemove?: boolean;
  handleMailClick?: (id: any) => void;
  disableMail?: boolean;
  onEditEmail?: (row: PaymentItem) => void;
  sx?: SxProps<Theme>;
  variant?: 'search' | 'confirm';
}

export interface PhysicianResult {
  firstName: string;
  lastName: string;
  license: string;
  balance: string;
  id: string;
  statusId: number;
  legalCanPay: boolean;
  participating?: boolean;
  pendingPaymentStatus?: string;
  maskedEmail?: string | null;
}

export interface PaymentItem extends PhysicianResult {
  amount: string | number;
  participating: boolean;
  participationLocked: boolean;
  baseBalance: number;
}

const ErrorToolTip = styled(({ className, open, ...props }: TooltipProps) => (
  <Tooltip open={open} {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#d32f2f',
    maxWidth: 120,
    fontSize: 12,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#d32f2f',
  },
}));

const InfoToolTip = styled(({ className, open, ...props }: TooltipProps) => (
  <Tooltip open={open} {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#ffb880',
    color: '#4f4f4f',
    fontSize: 15,
    padding: '10px',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#ffb880',
  },
}));

const BalanceToolTip = props => {
  if (props.color === 'error') return <ErrorToolTip {...props} />;
  return <InfoToolTip {...props} />;
};

const stopGridEvent = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};

interface PaymentAmountCellProps {
  row: PaymentItem;
  disabled: boolean;
  onAmountChange: (id: string, amount: string) => void;
}

const PaymentAmountCell: React.FC<PaymentAmountCellProps> = ({ row, disabled, onAmountChange }) => {
  const legalPayment = row.statusId === 6 && row.legalCanPay;
  const invalid = !isValidPaymentAmount(row.amount, row);
  if (disabled) {
    const amountText = (
      <Typography variant="body2" textAlign="right" width="100%">
        {formatToUSD(row.amount)}
      </Typography>
    );

    if (!legalPayment) {
      return amountText;
    }

    return (
      <BalanceToolTip
        open
        title="Please contact the Program at (804) 330-2471 to obtain your balance."
        arrow
        placement="bottom"
        color="info"
      >
        {amountText}
      </BalanceToolTip>
    );
  }

  return (
    <BalanceToolTip
      open={invalid}
      title={`Amount must be greater than ${formatToUSD(NON_PARTICIPATING_FEE)}. Overpayment of the assessment fee is allowed.`}
      arrow
      placement="right"
      color="error"
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        width="100%"
        onClick={stopGridEvent}
        onMouseDown={stopGridEvent}
        onKeyDown={stopGridEvent}
      >
        <Typography variant="body2" pr={0.5}>
          $
        </Typography>
        <NumberInput
          placeholder="Enter Amount..."
          allowNegative={false}
          decimalScale={2}
          fixedDecimalScale
          color="primary"
          size="small"
          variant="outlined"
          value={row.amount}
          error={invalid}
          sx={{
            width: 120,
            '& .MuiOutlinedInput-input': {
              textAlign: 'right',
              paddingRight: '8px',
            },
          }}
          onChange={event => {
            let newValue = event.target.value;

            if (Number.isNaN(newValue) || parseFloat(newValue) <= 0) {
              newValue = '';
            }

            onAmountChange(row.id, newValue);
          }}
        />
      </Box>
    </BalanceToolTip>
  );
};

export const PhysicianSearchGrid: React.FC<PhysicianSearchGridProps> = ({
  rows,
  setRows,
  handleDeleteClick,
  disableEditAndRemove,
  handleMailClick,
  disableMail,
  onEditEmail,
  sx,
  variant = 'search',
}) => {
  const isConfirm = variant === 'confirm';
  const isReadOnly = isConfirm || Boolean(disableEditAndRemove);
  const showPaymentFooter = !isConfirm;
  const totalAmount = calculateTotal(rows);
  const {
    isStatementEnabled,
    isLoading,
    toastOpen,
    toastMessage,
    toastSeverity,
    emailDialogOpen,
    emailRecipient,
    openEmailDialog,
    handleEmailConfirm,
    handleEmailDialogClose,
    handleToastClose,
  } = useEmailStatement({ onSent: id => handleMailClick?.(id) });
  const { handleAmountChange, handleParticipateToggle } = useAssessmentRowActions(setRows);

  const columns = React.useMemo<GridColumns<any>>(() => {
    const identityColumns: GridColumns<any> = [
      {
        field: 'license',
        headerName: 'License Number',
        flex: 1,
      },
      {
        field: 'firstName',
        headerName: 'First Name',
        flex: 1,
      },
      {
        field: 'lastName',
        headerName: 'Last Name',
        flex: 1,
      },
    ];

    const emailColumn = {
      field: 'maskedEmail',
      headerName: 'Email',
      flex: 1.2,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<PaymentItem['maskedEmail'], PaymentItem>) => (
        <MaskedEmailField
          maskedEmail={params.row.maskedEmail}
          name={formatProviderName(params.row.firstName, params.row.lastName)}
          onEdit={() => onEditEmail?.(params.row)}
        />
      ),
    };

    const amountColumn = {
      field: 'amount',
      headerName: 'Payment Amount',
      flex: 1,
      align: 'right' as const,
      headerAlign: 'right' as const,
      sortable: false,
      filterable: false,
      cellClassName: (params: GridCellParams) =>
        isReadOnly || !canEditPaymentAmount(params.row as PaymentItem) ? '' : 'payment-amount-editing',
      renderCell: (params: GridRenderCellParams<PaymentItem['amount'], PaymentItem>) => (
        <PaymentAmountCell
          row={params.row}
          disabled={isReadOnly || !canEditPaymentAmount(params.row)}
          onAmountChange={handleAmountChange}
        />
      ),
    };

    if (isConfirm) {
      return [...identityColumns, amountColumn];
    }

    return [
      ...identityColumns,
      emailColumn,
      {
        field: 'balance',
        headerName: 'Balance Due',
        flex: 1,
        align: 'right' as const,
        headerAlign: 'right' as const,
        renderCell: params => {
          if (params.value === 'legal') {
            return <Chip label="Under Legal Review" />;
          }
          return <>{formatToUSD(params.value)}</>;
        },
      },
      amountColumn,
      {
        field: 'actions',
        headerName: '',
        flex: 1.4,
        sortable: false,
        filterable: false,
        minWidth: 200,
        renderCell: (params: GridCellParams) => (
          <Grid container justifyContent="center" alignItems="center" wrap="nowrap" spacing={1}>
            <Grid item>
              <ParticipateToggleButton
                participating={params.row.participating}
                locked={params.row.participationLocked}
                onToggle={() => handleParticipateToggle(params.row.id)}
              />
            </Grid>
            {isStatementEnabled && (
              <Grid item>
                <Tooltip title="Email me a copy of my statement" arrow>
                  <span>
                    <IconButton
                      disabled={disableMail || isLoading}
                      size="medium"
                      color="primary"
                      onClick={event => {
                        event.stopPropagation();
                        openEmailDialog(params.row.id, formatProviderName(params.row.firstName, params.row.lastName));
                      }}
                    >
                      <Email />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
            )}
            <Grid item>
              <IconButton
                disabled={disableEditAndRemove}
                size="medium"
                sx={{ color: 'dustyRose.main' }}
                onClick={event => {
                  event.stopPropagation();
                  handleDeleteClick?.(params.row.id);
                }}
              >
                <DeleteOutlined />
              </IconButton>
            </Grid>
          </Grid>
        ),
      },
    ];
  }, [
    disableEditAndRemove,
    disableMail,
    handleAmountChange,
    handleDeleteClick,
    handleParticipateToggle,
    isConfirm,
    isLoading,
    isReadOnly,
    isStatementEnabled,
    onEditEmail,
    openEmailDialog,
  ]);

  return (
    <Box>
      <CustomDataGrid
        autoHeight
        components={{
          NoRowsOverlay: NoRowsOverlay,
          ...(showPaymentFooter ? { Footer: AssessmentPaymentFooter } : {}),
        }}
        componentsProps={
          showPaymentFooter
            ? {
                footer: {
                  totalAmount,
                },
              }
            : undefined
        }
        rowHeight={60}
        disableColumnMenu
        disableSelectionOnClick
        hideFooter={isConfirm}
        pageSize={10}
        rows={rows}
        columns={columns}
        getRowClassName={params => (rows.findIndex(row => row.id === params.id) % 2 === 1 ? 'row-striped' : '')}
        sx={{ ...sx }}
      />
      {isStatementEnabled && !isConfirm && (
        <>
          <Toast open={toastOpen} message={toastMessage} severity={toastSeverity} onClose={handleToastClose} />
          <EmailStatementDialog
            open={emailDialogOpen}
            onClose={handleEmailDialogClose}
            onConfirm={handleEmailConfirm}
            loading={isLoading}
            physicianName={emailRecipient?.name}
          />
        </>
      )}
    </Box>
  );
};
